import React, { useEffect, useState } from "react";
import { ref, onValue, remove } from "firebase/database";
import { db } from "../firebase";
import AddTeam from "./AddTeam";
import Confirm from "./Confirm";
import Popup from "./Popup";

const TeamList = ({ gameId }) => {
  const [teams, setTeams] = useState({});
  const [bidders, setBidders] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [confirm, setConfirm] = useState({ isOpen: false, message: '', title: '', type: 'warning', onConfirm: null });
  const [popup, setPopup] = useState({ isOpen: false, message: '', title: '', type: 'info' });

  useEffect(() => {
    const teamsRef = ref(db, `games/${gameId}/teams`);
    const usersRef = ref(db, 'users');

    const unsubscribeTeams = onValue(teamsRef, (snapshot) => {
      if (snapshot.exists()) setTeams(snapshot.val());
      else setTeams({});
    });

    const unsubscribeBidders = onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const users = snapshot.val();
        const biddersOnly = {};
        
        Object.keys(users).forEach((uid) => {
          if (users[uid].role === 'bidder') {
            biddersOnly[uid] = users[uid];
          }
        });
        
        setBidders(biddersOnly);
      } else {
        setBidders({});
      }
    });

    return () => {
      unsubscribeTeams();
      unsubscribeBidders();
    };
  }, [gameId]);

  const handleDelete = async (teamId) => {
    setConfirm({
      isOpen: true,
      title: 'Delete Team',
      message: 'Delete this team?',
      type: 'error',
      onConfirm: async () => {
        await remove(ref(db, `games/${gameId}/teams/${teamId}`));
        setConfirm({ isOpen: false, message: '', title: '', onConfirm: null });
      }
    });
  };

  const handleEdit = (teamId, teamData) => {
    setSelectedTeam({ id: teamId, ...teamData });
    setShowAddTeam(true);
  };

  const handleCloseEdit = () => {
    setSelectedTeam(null);
    setShowAddTeam(false);
  };

  const getBidderInfo = (captainUid) => {
    if (!captainUid) return "-";
    const bidder = bidders[captainUid];
    if (!bidder) return "Unknown";
    return `${bidder.name} (${bidder.email})`;
  };

  return (
    <div className="card p-4 shadow mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="bi bi-shield-fill me-2"></i>
          Teams List
        </h4>
        <button 
          className="btn btn-success"
          onClick={() => {
            setSelectedTeam(null);
            setShowAddTeam(!showAddTeam);
          }}
        >
          <i className="bi bi-plus-circle me-2"></i>
          {showAddTeam ? "Show Team List" : "Add Team"}
        </button>
      </div>

      {showAddTeam ? (
        <div className="mb-4">
          <AddTeam 
            gameId={gameId} 
            selectedTeam={selectedTeam}
            onClose={handleCloseEdit}
          />
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-striped align-middle">
            <thead className="table-primary">
              <tr>
                <th>#</th>
                <th>Logo</th>
                <th>Team Name</th>
                <th>Owner</th>
                <th>Captain</th>
                <th>Purse Left</th>
                <th>Total Players</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {Object.keys(teams).map((id, index) => {
                const t = teams[id];
                const playerCount = t.players ? Object.keys(t.players).length : 0;

                return (
                  <tr key={id}>
                  <td className="fw-semibold">{index + 1}</td>
                  <td>
                    {t.logo ? (
                      <img
                        src={t.logo}
                        alt={`${t.teamName} logo`}
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                          borderRadius: "50%",
                          border: "2px solid #667eea"
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          background: "#e9ecef",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <i className="bi bi-shield text-muted"></i>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                    <i className="bi bi-trophy-fill text-warning me-2"></i>
                    <span className="fw-bold">{t.teamName}</span>
                    </div>
                  </td>
                   <td>
                    <div className="d-flex align-items-center">
                    <i className="bi bi-person-badge-fill text-primary me-2"></i>
                    <span className="fw-bold">{getBidderInfo(t.bidderUid)}</span>
                    </div>
                  </td>
                  <td>
                    <span className="badge bg-secondary">
                    <i className="bi bi-person-fill me-1"></i>
                    {getBidderInfo(t.captainUid)}
                    </span>
                  </td>
                  <td className="fw-semibold text-success">৳ {t.purseLeft || 0}</td>
                  <td>
                    <span className="badge bg-info text-dark">{playerCount}</span>
                  </td>
                  <td className="text-muted">
                    {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "-"}
                  </td>
                  <td>
                    <div className="btn-group" role="group">
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleEdit(id, t)}
                      title="Edit Team"
                    >
                      <i className="bi bi-pencil-fill"></i>
                    </button>

                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(id)}
                      title="Delete Team"
                    >
                      <i className="bi bi-trash-fill"></i>
                    </button>
                    </div>
                  </td>
                  </tr>
                );
              })}

              {Object.keys(teams).length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No teams found. Add your first team!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Confirm
        isOpen={confirm.isOpen}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ isOpen: false, message: '', title: '', onConfirm: null })}
        message={confirm.message}
        title={confirm.title}
        type={confirm.type}
      />

      <Popup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ isOpen: false, message: '', title: '', type: 'info' })}
        message={popup.message}
        title={popup.title}
        type={popup.type}
      />
    </div>
  );
};

export default TeamList;